import { getSetting } from "./settings";
import { getLines, getNumberOfLines } from "./utils/stringUtils";

import shaderToy from "./shaderToy.glsl?raw";
import glsl1 from "./glsl1.glsl?raw";
import { getCachedModule, storeCachedModule } from "./storage2";

const AppScopeIncludes = {
  $shadertoy: shaderToy,
  $glsl1: glsl1,
} as Record<string, string>;

export const sessionResourceCache = new Map<string, string>();

export type IIncludesData = [
  lineNumber: number,
  linesCount: number,
  includePath: string,
  // @todo error
  // https://www.typescriptlang.org/play?#code/C4TwDgpgBMCuYBsIEYoF4oG0BQUoA8AuKAO1gFsAjCAJwBpcoRiyraG8AvFi6+xgPQCotGgHsajAHQyAFJgDuPNvSgBnAPzFYJACYQAZgEsSEXQF0oAHyxLSsBAjrrlfcwEps57NlCQY8EjIAKL4kADGwGboUIx4mESsfM7M9irO3EnsUEpZqpqEOvrGphbWcViJvNmpeRmEdTkNDk4ued7YQgI+ftBwiBAADDGYAIbEoyQgzpQTU84yUvLhrrSWNpi6hGrANCYA5h7mANw94H2BQ6ERUboj45PTULOPzivt1lgP88+Er1BbHZ7EiHY5AA
  // @ts-ignore
  ...([code: string, error?: undefined] | [code: null, error: string]),
];

// @note use g flag for .lastIndex
export const dependencyRegex = /^[ \t]*#[ \t]*include[ \t]+/g;

/**
 *
 * @param directiveValue part of the line after #include / #pragma glslify :
 * @returns
 */
// @note bruh since lygia's includes don't have ./ for relative imports, so modules should only be imported using <>, and process all quoted includes as relative or absolute
const extractDependencyPath = (directiveValue: string) => {
  let dependencyExtractedValue = directiveValue.match(
    /^(?:<([^>]*)>|(["'`]?)([^"'` ]*)\2)(?: *;? *)*$/
  );

  if (!dependencyExtractedValue) return null;

  let path = dependencyExtractedValue[1];

  // @note empty group would be undefined
  if (path !== undefined) {
    return {
      path,
      type: "module",
    } as const;
  }

  path = dependencyExtractedValue[3];

  return {
    path,
    type: /^https?:/.test(path) ? "url" : "relative",
  } as const;
};

interface IDependencyMeta {
  path: string;
  // editorLineNumber: number;
}

/**
 * Skip empty, "package" (snippet placeholder). Resolve `lygia` includes to GitHub links, resolve other modules to NPM provider, resolve relative paths
 * @param dependency
 * @param meta
 * @returns `null` if invalid path
 */
export const resolvePath = (
  { path, type }: Exclude<ReturnType<typeof extractDependencyPath>, null>,
  meta?: IDependencyMeta
) => {
  if (!path || (path === "package" && type === "module")) return null;

  let resultingPath: string;

  switch (type) {
    case "module":
      // @note built-in
      if (path.startsWith("$")) {
        return path;
      }
      // @note handle lygia separately because their package hasn't updated for years, so use code from gh directly
      else if (path.startsWith("lygia/")) {
        resultingPath =
          "https://raw.githubusercontent.com/patriciogonzalezvivo/lygia/main/" +
          path.slice("lygia/".length);
      } else {
        resultingPath = new URL(path, getSetting("npmPackageProvider")).href;

        // @note if we reference only module name, don't append .glsl, let npm package provider resolve "main" field instead
        if (path.indexOf("/") === -1) {
          return resultingPath;
        }
      }

      break;
    case "relative":
      if (meta) {
        resultingPath = new URL(path, meta.path).href;
      } else {
        throw "Invalid include path: " + path;
      }
      break;

    case "url":
    default:
      resultingPath = path;
      break;
  }

  if (!resultingPath.endsWith(".glsl")) resultingPath += ".glsl";

  return resultingPath;
};

/**
 * Tests if the line is a dependency and returns its path if found
 * @param line
 * @returns `undefined` when there's no dependency, `Object` when dep is parsed successfully and `null` if error
 */
export const checkForDependency = (line: string) => {
  dependencyRegex.lastIndex = 0;
  if (!dependencyRegex.exec(line)) return;

  try {
    return extractDependencyPath(line.slice(dependencyRegex.lastIndex));
  } catch {
    return null;
  }
};

/**
 *
 * @param shaderLines
 * @param currentIncludesData Output variable to store includes data for the provided shader code. Top level only (no nested includes)
 * @param meta
 * @returns
 */
export const processIncludes = async (
  shaderLines: Array<string>,
  currentIncludesData?: Array<IIncludesData> | null,
  meta?: IDependencyMeta
) => {
  if (currentIncludesData) currentIncludesData.length = 0;

  let resultLines = await Promise.all(
    shaderLines.map(async (line, lineIndex): Promise<string> => {
      let pathInfo = checkForDependency(line);

      if (pathInfo === undefined) return line;

      // invalid value
      if (pathInfo === null) {
        currentIncludesData?.push([
          1 + lineIndex,
          1,
          "",
          // @todo assign object string so it's always unique?
          null,
          "#include directive failed to load! Invalid syntax!",
        ]);

        return "";
      }

      // @note identical include already exists
      if (currentIncludesData?.find(([line, count, path]) => path === pathInfo.path)) {
        currentIncludesData?.push([
          1 + lineIndex,
          1,
          "",
          null,
          "This exact #include directive already specified above!",
        ]);

        return "";
      }

      // process deps

      let dependencyCode: string | undefined = AppScopeIncludes[pathInfo.path];

      if (dependencyCode) {
        currentIncludesData?.push([
          lineIndex + 1,
          getNumberOfLines(dependencyCode!),
          pathInfo.path,
          dependencyCode!,
        ]);

        return dependencyCode;
      }

      let pathUrl = resolvePath(pathInfo, meta);

      if (pathUrl === null) {
        currentIncludesData?.push([1 + lineIndex, 1, null, null, "Invalid #include value!"]);
        return "";
      }

      // @note remove npm cdn host, so the resources won't dupe if user changes the provider
      let resourceId = pathUrl.replace(getSetting("npmPackageProvider"), "@npm:");

      dependencyCode = sessionResourceCache.get(resourceId) ?? (await getCachedModule(resourceId));

      try {
        if (dependencyCode) {
          dependencyCode = await processIncludes(getLines(dependencyCode), null, { path: pathUrl });
        } else {
          dependencyCode = await fetch(pathUrl)
            .then(r => {
              if (r.ok) {
                return r.text();
              } else {
                throw new Error("#include directive failed to load!");
              }
            })
            .then(shader => {
              sessionResourceCache.set(resourceId, shader);

              if (getSetting("cachePackages")) {
                storeCachedModule({
                  url: resourceId,
                  code: shader,
                });
              }

              return processIncludes(getLines(shader), null, { path: pathUrl });
            });
        }
      } catch (e) {
        if (meta) {
          // @note exit on first inner failed include
          throw e;
        } else {
          currentIncludesData?.push([
            1 + lineIndex,
            1,
            pathUrl,
            null,
            `${e.message}\n\nIs the URL valid?\n${pathUrl}`,
          ]);

          return "";
        }
      }

      // @note skip further processing
      if (dependencyCode === "") return "";

      currentIncludesData?.push([
        lineIndex + 1,
        getNumberOfLines(dependencyCode!),
        pathUrl,
        dependencyCode!,
      ]);

      return dependencyCode!;
    })
  );

  return resultLines.join("\n");
};
